import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { googleSignIn } from '../lib/auth';
import api from '../lib/api';
import { trackEvent } from '../lib/analytics';

function Auth() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser, setSession } = useAuthStore();

  const handleSuccess = (data: any, provider: 'password' | 'google', authMode: 'signin' | 'signup') => {
    trackEvent('authentication_succeeded', { provider, mode: authMode });
    setUser(data.user);
    setSession(data.session || { access_token: data.token });
    if (data.user?.onboarding_complete) {
      navigate('/today');
    } else {
      navigate('/onboard');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!email) {
      setFieldErrors({ email: 'Email is required' });
      return;
    }
    if (!password) {
      setFieldErrors({ password: 'Password is required' });
      return;
    }
    if (mode === 'signup' && password.length < 8) {
      setFieldErrors({ password: 'Password must be at least 8 characters' });
      return;
    }

    setLoading(true);
    try {
      if (mode === 'signup') {
        const { data } = await api.post('/auth/signup', { email, password, name });
        handleSuccess(data, 'password', 'signup');
      } else {
        const { data } = await api.post('/auth/login', { email, password });
        handleSuccess(data, 'password', 'signin');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setError('');
    setLoading(true);
    try {
      const accessToken = await googleSignIn();
      const { data } = await api.post('/auth/google', { access_token: accessToken });
      handleSuccess(data, 'google', mode);
    } catch (err: any) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-4" style={{
      background: 'linear-gradient(to bottom, var(--lumen-bg-top, #F4F1EC), var(--lumen-bg-bottom, #EDE6F1))',
    }}>
      <div className="mb-10 text-center">
        <h1 className="font-fraunces" style={{ fontSize: 48, fontWeight: 300, color: 'var(--gold)', letterSpacing: '0.06em', marginBottom: 8 }}>
          <span style={{ fontWeight: 200 }}>A</span>XIS
        </h1>
        <p style={{ fontSize: 16, color: 'var(--text)', fontWeight: 300, letterSpacing: '0.02em' }}>Your AI Chief of Staff</p>
        <div style={{ width: 32, height: 2, background: 'var(--gold)', margin: '16px auto 0', borderRadius: 1 }} />
      </div>

      <div className="lumen-glass" style={{
        width: '100%',
        maxWidth: 380,
        borderRadius: 24,
        padding: '32px 28px',
      }}>
        <div style={{ display: 'flex', gap: 20, marginBottom: 24, justifyContent: 'center' }}>
          <button
            onClick={() => { setMode('signin'); setError(''); setFieldErrors({}); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 14, fontWeight: 600, paddingBottom: 6,
              color: mode === 'signin' ? 'var(--gold)' : 'var(--muted)',
              borderBottom: mode === 'signin' ? '2px solid var(--gold)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => { setMode('signup'); setError(''); setFieldErrors({}); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 14, fontWeight: 600, paddingBottom: 6,
              color: mode === 'signup' ? 'var(--gold)' : 'var(--muted)',
              borderBottom: mode === 'signup' ? '2px solid var(--gold)' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="Name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              style={{
                width: '100%', background: '#FFFFFF', border: '1px solid var(--border)',
                borderRadius: 14, padding: '14px 16px', fontSize: 15, color: 'var(--text)',
                fontFamily: 'inherit', outline: 'none', transition: 'border 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
            />
          )}

          <div>
            <input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%', background: '#FFFFFF', border: '1px solid var(--border)',
                borderRadius: 14, padding: '14px 16px', fontSize: 15, color: 'var(--text)',
                fontFamily: 'inherit', outline: 'none', transition: 'border 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
            />
            {fieldErrors.email && (
              <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.email}</p>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%', background: '#FFFFFF', border: '1px solid var(--border)',
                borderRadius: 14, padding: '14px 16px', paddingRight: 56, fontSize: 15,
                color: 'var(--text)', fontFamily: 'inherit', outline: 'none', transition: 'border 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--gold)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--muted)', fontSize: 13, fontFamily: 'inherit',
              }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
            {fieldErrors.password && (
              <p style={{ color: 'var(--danger)', fontSize: 12, marginTop: 4 }}>{fieldErrors.password}</p>
            )}
          </div>

          {error && (
            <p style={{ color: 'var(--danger)', fontSize: 13, textAlign: 'center' }}>{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, var(--gold-light), var(--gold))',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: 15,
              padding: '14px 0',
              borderRadius: 999,
              border: 'none',
              cursor: loading ? 'wait' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: '0 8px 22px rgba(232, 155, 48, 0.30)',
              opacity: loading ? 0.7 : 1,
              transition: 'opacity 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginTop: 4,
            }}
          >
            {loading ? (
              <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
            ) : (
              mode === 'signin' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        <button
          onClick={handleGoogleAuth}
          disabled={loading}
          style={{
            width: '100%',
            background: '#FFFFFF',
            color: 'var(--text)',
            fontWeight: 500,
            fontSize: 14,
            padding: '13px 0',
            borderRadius: 14,
            border: '1px solid var(--border)',
            cursor: loading ? 'wait' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 0.2s, background 0.2s',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default Auth;
